import { action, makeAutoObservable } from "mobx";
import { EffectDescription, FilterDescription, TitleDescription, TransitionDescription } from "../config/config";
import { MediaData } from "./CompositionStore";

export type ThemeVariants = "dark" | "light" | "system";

type DraggingObjectPayload =
    | {
    type: "media";
    payload: MediaData;
}
    | {
    type: "text-preset";
    payload: TitleDescription;
};

class ApplicationStoreInstance {
    isLoading: boolean = true;
    isRendering: boolean = false;
    selectedLibrettoClipId: string | null = null;
    selectedTransitionId: string | null = null;
    subtitlesLibrettoClipId: string | null = null;
    transitions: Record<string, TransitionDescription> = {};
    filters: Record<string, FilterDescription> = {};
    effects: Record<string, EffectDescription> = {};
    draggingObject: DraggingObjectPayload | null = null;
    selectedRange: {start: number; end: number} | null = null;
    rangeToolEnabled: boolean = false;
    theme: ThemeVariants = "system";
    filtersPath: string | null = null;
    effectsPath: string | null = null;
    transitionsPath: string | null = null;
    titlesPath: string | null = null;
    subtitleStylesPath: string | null = null;
    animationsPath: string | null = null;

    constructor() {
        makeAutoObservable(this);
    }

    setIsLoading(value: boolean) {
        this.isLoading = value;
    }

    setIsRendering(value: boolean) {
        this.isRendering = value;
    }

    setSelectedLibrettoClipId(id: string) {
        this.selectedLibrettoClipId = id;
        this.setSelectedTransitionId(null);
    }

    setSelectedLibrettoClipIdToNull() {
        ApplicationStore.setRangeToolEnabled(false);
        this.selectedLibrettoClipId = null;
        this.setSelectedTransitionId(null);
    }

    setSubtitlesLibrettoClipId(id: string | null) {
        this.subtitlesLibrettoClipId = id;
    }

    setSelectedTransitionId(id: string | null) {
        this.selectedTransitionId = id;
    }

    setDraggingObject(draggingObject: DraggingObjectPayload | null) {
        this.draggingObject = draggingObject;
    }

    setRangeToolEnabled(value: boolean) {
        this.rangeToolEnabled = value;
    }

    setSelectedRange(value: {start: number, end: number}) {
        this.selectedRange = value;
    }

    setTheme(value: ThemeVariants = "dark") {
        if (value === "system") {
            const isDarkTheme = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
            ApplicationStore.theme = isDarkTheme ? "dark" : "light";
        } else {
            ApplicationStore.theme = value;
        }
    }

    setFiltersPath(path: string | null) {
        this.filtersPath = path;
    }

    setEffectsPath(path: string | null) {
        this.effectsPath = path;
    }

    setTransitionsPath(path: string | null) {
        this.transitionsPath = path;
    }

    setSubtitleStylesPath(path: string | null) {
        this.subtitleStylesPath = path;
    }

    setAnimationsPath(path: string | null) {
        this.animationsPath = path;
    }

    setTitlesPath(path: string | null) {
        this.titlesPath = path;
    }

    setTransitions(transitions: Record<string, TransitionDescription>) {
        this.transitions = transitions;
    }

    setFilters(filters: Record<string, FilterDescription>) {
        this.filters = filters;
    }

    setEffects(effects: Record<string, EffectDescription>) {
        this.effects = effects;
    }

    reset() {
        this.isLoading = true;
        this.isRendering = false;
        this.selectedLibrettoClipId = null;
        this.selectedTransitionId = null;
        this.subtitlesLibrettoClipId = null;
        this.transitions = {};
        this.filters = {};
        this.effects = {};
        this.draggingObject = null;
        this.rangeToolEnabled = false;
        this.theme = "system";
        this.filtersPath = null;
        this.effectsPath = null;
        this.transitionsPath = null;
        this.titlesPath = null;
        this.subtitleStylesPath = null;
        this.animationsPath = null;
    }
}

const ApplicationStore = new ApplicationStoreInstance();

export { ApplicationStore };
